import React from 'react';
import { PaymentProfileUpdateConfig } from '../../../models/stores/Cart';
import {
  ConfigEnabledPaymentMethodContract,
  PaymentMethod as PaymentMethodWithFormContext,
} from '../../../types/Configuration';
import { CustomerPaymentProfileContract } from '../../../types/Customer';
import { PayPalAcpMethods, PayPalButtonMethods } from '../../../types/PayPal';
import { SpreedlyNewPaymentMethodEventHandlers } from '../../../types/Spreedly';
import { SpreedlyValues } from '../../../utils/constants';
import SavedCardList from '../SavedCardList';
import { AmazonPayCheckoutButtonProps } from '../../payment/AmazonPayCheckoutButton';
import PaymentMethodForm from '../PaymentMethodForm';

export const AuthenticatedPaymentMethodFormTestId =
  'AuthenticatedPaymentMethodForm-input';
export const AuthenticatedPaymentMethodFormNewMethodGroupLabelTestId =
  'NewMethodGroupLabel-AuthenticatedPaymentMethodForm-input';

export type AuthenticatedPaymentMethodFormProps = {
  hasRecurringBillingItem?: boolean;
  paymentProfiles: CustomerPaymentProfileContract[];
  paymentMethods: PaymentMethodWithFormContext[];
  payPalButtonMethods: PayPalButtonMethods;
  payPalAcpMethods?: PayPalAcpMethods;
  amazonPayButtonProps: AmazonPayCheckoutButtonProps;
  spreedlyEventHandlers?: SpreedlyNewPaymentMethodEventHandlers;
  cartTotal: number;
  onProfileSelected: (
    cardMethod: ConfigEnabledPaymentMethodContract,
    paymentConfig: PaymentProfileUpdateConfig,
    selectedProfileId: number
  ) => void;
  onPaymentMethodChanged?: (paymentMethodId: string | null) => void;
  hasGiftCards?: boolean;
};

const AuthenticatedPaymentMethodForm: React.FC<
  AuthenticatedPaymentMethodFormProps
> = ({
  hasRecurringBillingItem,
  paymentProfiles,
  paymentMethods,
  payPalButtonMethods,
  payPalAcpMethods,
  amazonPayButtonProps,
  spreedlyEventHandlers,
  onProfileSelected,
  onPaymentMethodChanged,
  cartTotal,
  hasGiftCards,
}: AuthenticatedPaymentMethodFormProps) => {
  const handleOnCardChosen = (id: number): void => {
    const chosenProfile = paymentProfiles.find(profile => profile.id === id);
    if (!chosenProfile) return;
    const methodId = parseInt(chosenProfile.card.cardType.toString(), 10);
    const cardConfig =
      spreedlyEventHandlers &&
      chosenProfile.card.cardTokenProvider?.toUpperCase() ===
        SpreedlyValues.CARD_TOKEN_PROVIDER
        ? {
            ...chosenProfile.card,
            spreedlyPaymentToken: chosenProfile.card.cardToken,
            paymentMethodFingerprint:
              chosenProfile.card.paymentMethodFingerprint,
          }
        : { ...chosenProfile.card };
    // TODO See note on Cart.updatePaymentProfile(). This change was part of TP-1051
    // While it does resolve the issue, there is definitely a better way to handle Stripe
    // new and saved cards. The refactor effort was too much for this fix, as QA is currently blocked.
    const paymentConfig =
      chosenProfile.card.cardTokenProvider?.toUpperCase() === 'STRIPE'
        ? {
            cardConfig,
            stripeConfig: {
              paymentMethodId: chosenProfile.card.cardToken,
              isSavedCard: true,
              cardBrand: chosenProfile.card.cardBrand,
              last4: chosenProfile.card.last4,
              expMonth: chosenProfile.card.expMonth,
              expYear: chosenProfile.card.expYear,
            },
          }
        : { cardConfig };
    onProfileSelected(
      {
        id: methodId,
        method: chosenProfile.card.cardTypeName,
        methodType: chosenProfile.method,
      },
      paymentConfig,
      chosenProfile.id ?? 0
    );
  };

  if (hasGiftCards && cartTotal === 0) {
    return (
      <div
        className="flex flex-col"
        data-testid={AuthenticatedPaymentMethodFormTestId}
      >
        <div
          className="text-sm ml-2 mt-3 mb-2"
          data-testid={AuthenticatedPaymentMethodFormNewMethodGroupLabelTestId}
        >
          No additional payment method required
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      data-testid={AuthenticatedPaymentMethodFormTestId}
    >
      {paymentProfiles.length > 0 ? (
        <>
          <div className="text-sm ml-2 mb-2">Choose a saved credit card</div>
          <SavedCardList
            paymentProfiles={paymentProfiles}
            onCardChosen={handleOnCardChosen}
          />

          <div
            className="text-sm ml-2 mt-3 mb-2"
            data-testid={
              AuthenticatedPaymentMethodFormNewMethodGroupLabelTestId
            }
          >
            Or enter a different payment method
          </div>
        </>
      ) : (
        <div
          className="text-sm ml-2 mt-3 mb-2"
          data-testid={AuthenticatedPaymentMethodFormNewMethodGroupLabelTestId}
        >
          Choose a payment method below
        </div>
      )}

      <PaymentMethodForm
        onPaymentMethodChanged={onPaymentMethodChanged}
        hasRecurringBillingItem={hasRecurringBillingItem}
        paymentMethods={paymentMethods}
        payPalButtonMethods={payPalButtonMethods}
        payPalAcpMethods={payPalAcpMethods}
        amazonPayButtonProps={amazonPayButtonProps}
        spreedlyEventHandlers={spreedlyEventHandlers}
      />
    </div>
  );
};

export default AuthenticatedPaymentMethodForm;
